/**
 * Forward-leaning jockey draw routine. Split out of horse-procedural-parts
 * so each module stays under the recommended line budget.
 *
 * Visual elements (in draw order):
 *   - White breeches + black boot.
 *   - Silk torso (the per-lane silkColor — primary recognisable element).
 *   - Diagonal white sash + cuff stripe (silks pattern).
 *   - Forward-extended arm gripping reins.
 *   - Helmet matching silk colour with a black visor + lane number.
 */

import type { RenderArgs } from './horse-procedural-parts.js';

interface JockeyAnchor { torsoX: number; torsoY: number; }

export function drawJockey(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const crouchY = a.frame === 2 ? -3 : 0;
  const anchor: JockeyAnchor = { torsoX: 0, torsoY: -a.thick / 2 - 12 + crouchY };
  drawJockeyLegs(c, anchor);
  drawJockeyTorso(c, anchor, a.silkColor);
  drawJockeyArm(c, anchor, a.silkColor, a.len, a.thick);
  drawJockeyHelmet(c, anchor, a.silkColor, a.number);
}

function drawJockeyLegs(c: CanvasRenderingContext2D, an: JockeyAnchor): void {
  const { torsoX, torsoY } = an;
  // Pants/breeches (white).
  c.fillStyle = '#f4ece0';
  c.beginPath();
  c.moveTo(torsoX - 16, torsoY + 6);
  c.quadraticCurveTo(torsoX - 8, torsoY + 14, torsoX + 4, torsoY + 12);
  c.lineTo(torsoX + 6, torsoY + 6);
  c.lineTo(torsoX - 10, torsoY + 4);
  c.closePath();
  c.fill();
  // Boot (black).
  c.fillStyle = '#0d0703';
  c.beginPath();
  c.moveTo(torsoX - 18, torsoY + 8);
  c.quadraticCurveTo(torsoX - 14, torsoY + 18, torsoX - 6, torsoY + 18);
  c.lineTo(torsoX - 4, torsoY + 14);
  c.lineTo(torsoX - 16, torsoY + 12);
  c.closePath();
  c.fill();
}

function drawJockeyTorso(c: CanvasRenderingContext2D, an: JockeyAnchor, silkColor: string): void {
  const { torsoX, torsoY } = an;
  c.fillStyle = silkColor;
  c.beginPath();
  c.moveTo(torsoX - 16, torsoY + 6);
  c.lineTo(torsoX + 14, torsoY + 6);
  c.lineTo(torsoX + 16, torsoY - 14);
  c.quadraticCurveTo(torsoX, torsoY - 22, torsoX - 14, torsoY - 14);
  c.closePath();
  c.fill();
  // Diagonal sash (white).
  c.fillStyle = 'rgba(255,255,255,0.85)';
  c.beginPath();
  c.moveTo(torsoX - 14, torsoY - 6);
  c.lineTo(torsoX - 10, torsoY - 12);
  c.lineTo(torsoX + 16, torsoY + 2);
  c.lineTo(torsoX + 12, torsoY + 6);
  c.closePath();
  c.fill();
  // Cuff stripe.
  c.fillStyle = 'rgba(255,255,255,0.7)';
  c.fillRect(torsoX + 12, torsoY - 14, 4, 4);
  // Torso shadow underline.
  c.fillStyle = 'rgba(0,0,0,0.25)';
  c.beginPath();
  c.moveTo(torsoX - 16, torsoY + 4);
  c.lineTo(torsoX + 14, torsoY + 4);
  c.lineTo(torsoX + 14, torsoY + 6);
  c.lineTo(torsoX - 16, torsoY + 6);
  c.closePath();
  c.fill();
}

function drawJockeyArm(
  c: CanvasRenderingContext2D,
  an: JockeyAnchor,
  silkColor: string,
  len: number,
  thick: number,
): void {
  const { torsoX, torsoY } = an;
  // Arm forward gripping reins.
  c.strokeStyle = silkColor;
  c.lineWidth = 6;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(torsoX + 8, torsoY - 8);
  c.quadraticCurveTo(torsoX + 22, torsoY - 6, torsoX + 30, torsoY + 2);
  c.stroke();
  // Glove (black).
  c.fillStyle = '#0d0703';
  c.beginPath();
  c.arc(torsoX + 30, torsoY + 2, 3.5, 0, Math.PI * 2);
  c.fill();
  // Reins to bridle.
  c.strokeStyle = 'rgba(40,20,10,0.85)';
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(torsoX + 30, torsoY + 2);
  c.lineTo(len / 2 + 22, -thick / 2 - 16);
  c.stroke();
}

function drawJockeyHelmet(
  c: CanvasRenderingContext2D,
  an: JockeyAnchor,
  silkColor: string,
  number: number,
): void {
  const helmetX = an.torsoX - 4;
  const helmetY = an.torsoY - 22;
  c.fillStyle = silkColor;
  c.beginPath();
  c.arc(helmetX, helmetY, 13, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = 'rgba(0,0,0,0.55)';
  c.lineWidth = 1.5;
  c.stroke();
  // Visor.
  c.fillStyle = 'rgba(0,0,0,0.78)';
  c.beginPath();
  c.ellipse(helmetX + 3, helmetY - 1, 14, 5, 0.05, 0, Math.PI * 2);
  c.fill();
  // Highlight.
  c.fillStyle = 'rgba(255,255,255,0.35)';
  c.beginPath();
  c.arc(helmetX - 5, helmetY - 5, 3.5, 0, Math.PI * 2);
  c.fill();
  // Lane number.
  c.fillStyle = '#000';
  c.font = 'bold 14px Oswald, sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(String(number), helmetX, helmetY + 4);
}
